import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentUser } from '@/lib/hooks'
import { HAS_AUTH } from '@/lib/auth'
import { Enrich } from '@/views/Enrich'

interface EnrichmentGateProps {
  children: ReactNode
}

// =============================================================================
// EnrichmentGate — first-login opt-in profile scan.
// =============================================================================
// Mounts between SubscriptionGuard and the dashboard content. If the user is
// subscribed and has no enrichment record yet, takes over the whole viewport
// with the Enrich.tsx flow. Once they finish (`complete`/`skipped`/`failed`),
// the gate lets the children render and never shows again.
// =============================================================================
export function EnrichmentGate({ children }: EnrichmentGateProps) {
  const queryClient = useQueryClient()
  const { data: user, isLoading } = useCurrentUser()

  if (!HAS_AUTH) return <>{children}</>
  if (isLoading) return <>{children}</>          // SubscriptionGuard already rendered a loader
  if (!user) return <>{children}</>              // No user document — bail to children, will be handled elsewhere
  if (!user.jarvisEnabled) return <>{children}</> // Not subscribed — SubscriptionGuard already routed to /billing
  if (user.enrichment) return <>{children}</>    // Already ran (complete, skipped, failed, pending) — never re-gate

  const handleComplete = () => {
    // The Enrich component already PATCHes its own state; we just need to
    // refetch so jarvisEnabled+enrichment are fresh and the gate falls through.
    void queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
  }

  return <Enrich onComplete={handleComplete} />
}
